/* eslint react/prop-types: 0 */
import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Typography,
  TablePagination,
  Skeleton,
  Card,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DataGridPro } from '@mui/x-data-grid-pro';
import ResponsiveListCard from './ResponsiveListCard';

const DEFAULT_BREAKPOINT = 'md';
const DEFAULT_DESKTOP_HEIGHT = 'calc(100vh - 200px)';

// Stable fallback so consumers that omit mobileCardProps don't cause
// a new object reference on every render.
const EMPTY_CARD_PROPS = {};

const defaultGridSx = {
  '& .MuiDataGrid-cell': {
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
  },
};

// Extract a value from a row using a column's valueGetter when available,
// falling back to direct field access.
const getColumnValue = (column, row) => {
  if (!column) return row?.[row && Object.keys(row)[0]];
  if (typeof column.valueGetter === 'function') {
    try {
      return column.valueGetter({ row, value: row?.[column.field], field: column.field });
    } catch {
      return row?.[column.field];
    }
  }
  return row?.[column.field];
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const as = String(a);
  const bs = String(b);
  return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * ResponsiveList
 *
 * Renders DataGridPro above `mobileBreakpoint` and a mobile-friendly card
 * list below it. The desktop view forwards every grid prop untouched so
 * server-mode pagination/sort/filter pages work exactly as today. On mobile
 * it surfaces a sort Select + direction toggle and a TablePagination footer,
 * both wired to the same sortModel/paginationModel callbacks the grid uses.
 *
 * Any prop not listed below is forwarded to DataGridPro on desktop.
 *
 * preferDesktopGridOnMobile: when true below mobileBreakpoint, render the same
 * DataGrid as wide view (horizontal scroll on narrow screens).
 */
function ResponsiveList({
  rows = [],
  columns = [],
  rowCount,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  paginationMode = 'client',
  sortingMode = 'client',
  sortModel,
  onSortModelChange,
  pageSizeOptions = [25, 50, 100],
  getRowId,
  onRowClick,
  mobileBreakpoint = DEFAULT_BREAKPOINT,
  renderMobileCard,
  mobileCardProps,
  mobileSortOptions,
  mobileEmptyMessage = 'No items to display',
  mobileSkeletonCount = 4,
  desktopHeight = DEFAULT_DESKTOP_HEIGHT,
  desktopSx,
  preferDesktopGridOnMobile = false,
  ...dataGridProps
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  const useDataGrid = !isMobile || preferDesktopGridOnMobile;

  const rowIdOf = useMemo(
    () => getRowId || ((row) => row?.id),
    [getRowId]
  );

  // Only build the field lookup map on mobile — it is never used by the
  // desktop DataGridPro path, so skipping it avoids an O(n) forEach on
  // every render for desktop users.
  const columnByField = useMemo(() => {
    if (useDataGrid) return {};
    const map = {};
    columns.forEach((c) => {
      if (c?.field) map[c.field] = c;
    });
    return map;
  }, [useDataGrid, columns]);

  // Extract primitives so currentSort stays reference-stable across renders
  // where the parent passes sortModel as an inline array literal. String
  // values compare by value, preventing sortedRows from re-sorting when
  // the sort hasn't actually changed.
  const currentSortField = sortModel?.[0]?.field ?? null;
  const currentSortDir = sortModel?.[0]?.sort ?? null;
  const currentSort = useMemo(
    () => (currentSortField ? { field: currentSortField, sort: currentSortDir } : null),
    [currentSortField, currentSortDir]
  );

  const sortedRows = useMemo(() => {
    if (useDataGrid) return rows;
    if (sortingMode !== 'client' || !currentSort?.field) return rows;
    const column = columnByField[currentSort.field];
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = getColumnValue(column, a);
      const vb = getColumnValue(column, b);
      const cmp = compareValues(va, vb);
      return currentSort.sort === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [useDataGrid, rows, sortingMode, currentSort, columnByField]);

  const pagedRows = useMemo(() => {
    if (useDataGrid) return sortedRows;
    if (paginationMode !== 'client' || !paginationModel) return sortedRows;
    const start = paginationModel.page * paginationModel.pageSize;
    return sortedRows.slice(start, start + paginationModel.pageSize);
  }, [useDataGrid, sortedRows, paginationMode, paginationModel]);

  const total = useMemo(() => {
    if (paginationMode === 'client') return sortedRows.length;
    if (typeof rowCount === 'number') return rowCount;
    return rows.length;
  }, [paginationMode, sortedRows.length, rowCount, rows.length]);

  const sortFieldOptions = useMemo(() => {
    if (mobileSortOptions) return mobileSortOptions;
    return columns
      .filter((c) => c && c.field && c.sortable !== false && c.field !== 'actions')
      .map((c) => ({ field: c.field, label: c.headerName || c.field }));
  }, [columns, mobileSortOptions]);

  const handleSortFieldChange = useCallback((event) => {
    const field = event.target.value;
    const direction = currentSort?.sort || 'desc';
    onSortModelChange?.([{ field, sort: direction }]);
  }, [currentSort, onSortModelChange]);

  const handleToggleSortDirection = useCallback(() => {
    if (!currentSort?.field) return;
    const nextSort = currentSort.sort === 'asc' ? 'desc' : 'asc';
    onSortModelChange?.([{ field: currentSort.field, sort: nextSort }]);
  }, [currentSort, onSortModelChange]);

  const handlePageChange = useCallback((_event, newPage) => {
    onPaginationModelChange?.({
      page: newPage,
      pageSize: paginationModel?.pageSize || pageSizeOptions[0],
    });
  }, [onPaginationModelChange, paginationModel, pageSizeOptions]);

  const handlePageSizeChange = useCallback((event) => {
    onPaginationModelChange?.({
      page: 0,
      pageSize: parseInt(event.target.value, 10),
    });
  }, [onPaginationModelChange]);

  // Pre-build the skeleton index array once per mobileSkeletonCount change
  // rather than allocating a new array every loading render.
  const skeletonIndices = useMemo(
    () => Array.from({ length: mobileSkeletonCount }, (_, i) => i),
    [mobileSkeletonCount]
  );

  if (useDataGrid) {
    return (
      <Box sx={{ height: desktopHeight, width: '100%', ...desktopSx }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          paginationMode={paginationMode}
          sortingMode={sortingMode}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          pageSizeOptions={pageSizeOptions}
          getRowId={getRowId}
          onRowClick={onRowClick}
          pagination
          disableSelectionOnClick
          {...dataGridProps}
          sx={{ ...defaultGridSx, ...(dataGridProps.sx || {}) }}
        />
      </Box>
    );
  }

  const canShowSort = sortFieldOptions.length > 0 && onSortModelChange;
  const canShowPagination = Boolean(paginationModel && onPaginationModelChange);

  return (
    <Box sx={{ width: '100%' }}>
      {canShowSort && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: '10px',
          }}
        >
          <FormControl
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <InputLabel id="responsive-list-sort-label">Sort by</InputLabel>
            <Select
              labelId="responsive-list-sort-label"
              label="Sort by"
              value={currentSort?.field || ''}
              onChange={handleSortFieldChange}
            >
              {sortFieldOptions.map((opt) => (
                <MenuItem
                  key={opt.field}
                  value={opt.field}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={currentSort?.sort === 'asc' ? 'Ascending' : 'Descending'}>
            <span>
              <IconButton
                size="small"
                onClick={handleToggleSortDirection}
                disabled={!currentSort?.field}
                aria-label="Toggle sort direction"
              >
                {currentSort?.sort === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {loading && (
        <Stack spacing={1.5}>
          {skeletonIndices.map((idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card
              key={idx}
              sx={{ p: 2 }}
            >
              <Skeleton
                variant="text"
                width="60%"
                height={24}
              />
              <Skeleton
                variant="text"
                width="40%"
              />
              <Skeleton
                variant="text"
                width="80%"
              />
            </Card>
          ))}
        </Stack>
      )}

      {!loading && pagedRows.length === 0 && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            {mobileEmptyMessage}
          </Typography>
        </Card>
      )}

      {!loading && pagedRows.length > 0 && (
        <Stack spacing={1.5}>
          {pagedRows.map((row) => {
            const id = rowIdOf(row);
            const handleClick = onRowClick ? () => onRowClick({ row, id }) : undefined;
            if (renderMobileCard) {
              const node = renderMobileCard(row, { onClick: handleClick, id });
              return <React.Fragment key={id}>{node}</React.Fragment>;
            }
            return (
              <ResponsiveListCard
                key={id}
                row={row}
                onClick={handleClick}
                columns={columns}
                {...(mobileCardProps || EMPTY_CARD_PROPS)}
              />
            );
          })}
        </Stack>
      )}

      {canShowPagination && (
        <TablePagination
          component="div"
          count={total}
          page={paginationModel.page}
          rowsPerPage={paginationModel.pageSize}
          rowsPerPageOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      )}
    </Box>
  );
}

export default ResponsiveList;
